import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Eye, ShieldCheck, Heart, Wrench, CreditCard } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { UnifiedTiers, websiteCards, marketingCards } from "@/components/pricing/UnifiedTiers";
import { PackageBuilder } from "@/components/pricing/PackageBuilder";
import { OneTimeProjectBox } from "@/components/pricing/OneTimeProjectBox";
import { PricingFaq, PRICING_FAQS } from "@/components/pricing/PricingFaq";
import { Button } from "@/components/ui/button";
import { PackageTrackProvider } from "@/components/pricing/usePackageTrack";
import { setBuilderMode } from "@/components/pricing/builderMode";
import { SERVICE_PRICING } from "@/lib/pricingData";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { BoutiqueAdvantage } from "@/components/BoutiqueAdvantage";
import { PricingQuoteForm } from "@/components/pricing/PricingQuoteForm";



export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Website & Marketing Pricing | Colorado Web Impressions" },
      { name: "description", content: "Compare websites from $1,850, monthly marketing from $950, and repairs at $250/hour. All website packages include one year of hosting." },
      { property: "og:title", content: "Website & Marketing Pricing | Colorado Web Impressions" },
      { property: "og:description", content: "Websites from $1,850. Marketing from $950/month. Edits and repairs $250/hour. Compare all packages from Colorado Web Impressions." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://coloradowebimpressions.com/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          provider: {
            "@type": "LocalBusiness",
            name: "Colorado Web Impressions",
            areaServed: "Colorado",
            telephone: "+1-719-419-3935",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Digital Marketing Services",
            itemListElement: SERVICE_PRICING.map((s) => ({
              "@type": "Offer",
              name: s.service,
              description: s.oneLiner,
              price: s.startingAt.replace(/[^0-9]/g, ""),
              priceCurrency: "USD",
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "How Colorado Web Impressions Prices Web Design & SEO",
          description:
            "Chris Heidlebaugh explains how Colorado Web Impressions structures honest, public pricing for web design, SEO, PPC, and lead generation — no contracts, no sales pitch.",
          thumbnailUrl: "https://i.ytimg.com/vi/h7oKJnptSrE/maxresdefault.jpg",
          uploadDate: "2024-01-01",
          embedUrl: "https://www.youtube.com/embed/h7oKJnptSrE",
          contentUrl: "https://www.youtube.com/watch?v=h7oKJnptSrE",
          publisher: {
            "@type": "Organization",
            name: "Colorado Web Impressions",
            logo: {
              "@type": "ImageObject",
              url: "https://coloradowebimpressions.com/logo.png",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Web Design & Lead Generation",
          provider: { "@id": "https://coloradowebimpressions.com/#organization" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Colorado Web Impressions Packages",
            itemListElement: [...websiteCards, ...marketingCards].map((t) => ({
              "@type": "Offer",
              name: t.name,
              description: t.desc,
              price: t.price.replace(/[^0-9]/g, ""),
              priceCurrency: "USD",
              category: t.period,
              ...(t.monthlyPrice
                ? {
                    priceSpecification: {
                      "@type": "UnitPriceSpecification",
                      price: t.monthlyPrice.replace(/[^0-9]/g, ""),
                      priceCurrency: "USD",
                      unitCode: "MON",
                    },
                  }
                : {}),
              ...(t.oneTimePrice
                ? {
                    priceSpecification: {
                      "@type": "UnitPriceSpecification",
                      price: t.oneTimePrice.replace(/[^0-9]/g, ""),
                      priceCurrency: "USD",
                    },
                  }
                : {}),
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: PRICING_FAQS.flatMap((b) => b.items).map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/pricing" },
      ]),
    ],
  }),
  component: PricingHub,
});

function PricingHub() {
  return (
    <PackageTrackProvider>
      <PricingHubInner />
    </PackageTrackProvider>
  );
}


function PricingHubInner() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Web Design & SEO Pricing"
        title="Website & Marketing Pricing"
        belowTitle={
          <span className="text-gradient-gold text-base sm:text-lg font-semibold">
            Website builds. Monthly marketing. One-time help.
          </span>
        }
        description="Choose a one-time website package or monthly marketing plan. Clear inclusions, public prices, and direct access to Chris."
        hideMeetChris
        hideTrustList
      >
        <div className="mx-auto flex max-w-xl flex-col items-stretch justify-center gap-3 sm:flex-row">
          <a
            href="#website-pricing"
            onClick={() => setBuilderMode("web")}
            className="group inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            I Need a Website
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="#lead-generation"
            onClick={() => setBuilderMode("leads")}
            className="group inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gradient-peak px-6 py-3.5 text-base font-bold text-brand-cream shadow-peak transition-spring hover:-translate-y-0.5"
          >
            I Need Marketing
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-3 flex justify-center">
          <Button asChild variant="outline" className="h-auto border-brand-cream/30 bg-brand-cream/5 px-6 py-3 text-sm font-semibold text-brand-cream hover:bg-brand-cream/10 hover:text-brand-cream">
          <a
            href="#add-ons"
            onClick={() => setBuilderMode("custom")}
          >
            <Wrench className="h-4 w-4" />
            Build a Plan
            <ArrowRight className="h-4 w-4" />
          </a>
          </Button>
        </div>
      </PageHero>

      {/* Three intent-based packages */}
      <UnifiedTiers />

      <PackageBuilder />

      <PricingQuoteForm />

      {/* One-time hourly project — dark contrast band */}
      <OneTimeProjectBox />

      {/* Boutique-vs-bargain reframe — addresses "too cheap / small team" objections after tiers */}
      <BoutiqueAdvantage
        eyebrow="Colorado's hands-on web team"
        title={
          <>
            The best team in Colorado for <span className="text-gradient-gold">small business growth</span>.
          </>
        }
      />

      <PricingFaq />




      {/* Trust strip */}
      <section className="bg-background py-8 lg:py-12">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Eye,
                t: "Pricing posted publicly",
                d: "Real pricing is on this site. No \"we have to talk to you first\" or \" your needs are unique\" run-around. No salespeople or pitches.",
                tag: "Total transparency",
              },
              {
                icon: ShieldCheck,
                t: "No long contracts",
                d: "Month-to-month lead generation services. If we stop earning your business, you can leave any time. The way it should be.",
                tag: "Zero lock-in",
              },
              {
                icon: Heart,
                t: "No high-pressure pitch",
                d: "If we're not the right fit for your goals, business or budget, we'll tell you up front. We're not here to waste your time or money.",
                tag: "Honest answers",
              },
              {
                icon: CreditCard,
                t: "In-house financing for startups",
                d: "Starting at 4 monthly payments for a new website. Contact us to learn more about flexible payment plans built for new businesses.",
                tag: "Startup-friendly",
              },
            ].map((b) => (
              <div
                key={b.t}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/40 hover:shadow-gold"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-gold opacity-0 transition-smooth group-hover:opacity-100" />
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black shadow-gold">
                  <b.icon className="h-6 w-6" />
                </div>
                <div className="mt-5 text-[10px] font-bold uppercase tracking-widest text-brand-gold">
                  {b.tag}
                </div>
                <div className="mt-1.5 text-lg font-extrabold tracking-tight text-foreground">
                  {b.t}
                </div>
                <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Quick recommender quiz */}



      <FinalCTA />
    </SiteLayout>
  );
}
